/** Own nav type, not the facility's: a shared id union would let one cockpit's
 *  sections leak into the other. */
export type DealNavItem = { id: DealSection; label: string; ready: boolean };
export type DealNavGroup = { label: string; items: DealNavItem[] };

/**
 * The Deal cockpit's sections, read from the investor's side: whether the
 * contract is covered, paid and within its covenants, and what secures it.
 *
 * Collateral reads every pool of the contract, one facility at a time or all
 * together: pools are disjoint (a loan is pledged to one facility), so amounts
 * and counts add up, while ratios are recomputed from their parts.
 */
export const DEAL_NAV: DealNavGroup = {
  label: "Deal",
  items: [
    { id: "overview", label: "Overview", ready: true },
    // What the contract owes on each payment date, rung by rung, with the
    // ledger of draws and amortisations and the reserve it requires.
    { id: "payments", label: "Payments and reserve", ready: true },
    { id: "covenants", label: "Covenants", ready: true },
    { id: "collateral", label: "Collateral", ready: true },
    // Only what changed the contract's standing: verdicts, consequences, draws.
    { id: "events", label: "Events", ready: true },
    { id: "terms", label: "Terms", ready: true },
    { id: "cash", label: "Cash", ready: true },
  ],
};

/** What a non-admin contract grant opens. */
const NON_ADMIN_SECTIONS = new Set(["overview", "payments", "covenants", "collateral", "events", "terms"]);

/** What an anonymous grant opens. Cash is refused, not served emptied: statement
 *  lines name their holder and have no anonymous form. Terms too: an anonymous
 *  grant is served no contract configuration, so the page would only be empty. */
const ANON_SECTIONS = new Set(["overview", "payments", "covenants", "collateral", "events"]);

export const NON_ADMIN_DEAL_NAV: DealNavGroup = {
  label: "Deal",
  items: DEAL_NAV.items.filter((i) => NON_ADMIN_SECTIONS.has(i.id)),
};

export const ANON_DEAL_NAV: DealNavGroup = {
  label: "Deal",
  items: DEAL_NAV.items.filter((i) => ANON_SECTIONS.has(i.id)),
};

export type DealSection = "overview" | "payments" | "covenants" | "collateral" | "events" | "terms" | "cash";

export const DEAL_SECTION_TITLES: Record<DealSection, string> = {
  overview: "Overview",
  payments: "Payments and reserve",
  covenants: "Covenants",
  collateral: "Collateral",
  events: "Events",
  terms: "Terms",
  cash: "Cash",
};

const CANONICAL = new Set(Object.keys(DEAL_SECTION_TITLES));

/** Legacy section ids still reachable by link, mapped to their current section. */
const DEAL_SECTION_REDIRECTS: Record<string, DealSection> = {
  waterfall: "payments",
  obligation: "payments",
  config: "terms",
  "payment-report": "payments",
  distributions: "payments",
  aggregate: "overview",
  limits: "payments",
  facilities: "overview",
};

export function resolveDealSection(raw: string | null | undefined): DealSection {
  if (raw && CANONICAL.has(raw)) return raw as DealSection;
  if (raw && raw in DEAL_SECTION_REDIRECTS) return DEAL_SECTION_REDIRECTS[raw];
  return "overview";
}

/** Section ids a reader may not reach by URL. Derived from the nav so
 *  the two cannot drift; every endpoint behind them carries its own guard. */
export function restrictedDealSections(anonymous: boolean): Set<string> {
  const allowed = new Set((anonymous ? ANON_DEAL_NAV : NON_ADMIN_DEAL_NAV).items.map((i) => i.id));
  return new Set(DEAL_NAV.items.map((i) => i.id).filter((id) => !allowed.has(id)));
}
